import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Loader2, Truck, CheckCircle2, Clock } from "lucide-react";

export default function RenewPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [renewing, setRenewing] = useState(false);
  const [done, setDone] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/renew/${token}`);
      setInfo(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Invalid link");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const renew = async () => {
    setRenewing(true);
    try {
      const { data } = await api.post(`/renew/${token}`);
      setDone(data);
      load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Renew failed");
    } finally {
      setRenewing(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
      <Card className="w-full max-w-md border-slate-200 bg-white p-8 shadow-xl">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#A0148C]">
            <Truck className="h-7 w-7 text-white" />
          </div>
          <div>
            <p className="font-heading text-lg font-black uppercase text-slate-900">
              {info?.businessName || "Discount Truck Parking"}
            </p>
            <p className="text-sm font-bold tracking-widest text-[#A0148C]">RENEW PARKING</p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="h-8 w-8 animate-spin text-[#A0148C]" />
          </div>
        ) : error ? (
          <p data-testid="renew-error" className="rounded-lg border border-red-500/40 bg-red-950/40 p-4 text-red-300">
            {error}
          </p>
        ) : done ? (
          <div data-testid="renew-success" className="text-center">
            <CheckCircle2 className="mx-auto mb-3 h-14 w-14 text-emerald-400" />
            <h2 className="font-heading text-2xl font-bold text-slate-900">Renewed!</h2>
            <p className="mt-2 text-slate-500">
              Ticket #{done.ticketNumber || info?.ticketNumber} · Charged ${done.charged.toFixed(2)}. New expiry:
            </p>
            <p className="font-data text-lg text-slate-900">
              {new Date(done.newExpiresAt).toLocaleString()}
            </p>
          </div>
        ) : (
          <>
            <div className="mb-6 space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-4">
              {info.ticketNumber && <Row label="Ticket #" value={<span data-testid="renew-ticket">{info.ticketNumber}</span>} />}
              <Row label="Lot" value={info.lotName} />
              <Row label="Spot" value={`#${info.spotNumber}`} />
              <Row
                label="Expires"
                value={new Date(info.expiresAt).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
              />
              <div className="border-t border-slate-200 pt-3">
                <Row label="Base" value={`$${info.baseRate.toFixed(2)}`} />
                <Row label={`Sales tax (${info.taxPercent ?? 0}%)`} value={`$${(info.tax ?? 0).toFixed(2)}`} />
                <Row label="Total" value={`$${info.total.toFixed(2)}`} bold />
              </div>
            </div>

            <Button
              data-testid="renew-confirm"
              onClick={renew}
              disabled={renewing}
              className="h-14 w-full gap-2 bg-[#A0148C] text-lg font-bold hover:bg-[#80106F]"
            >
              {renewing ? <Loader2 className="h-5 w-5 animate-spin" /> : <Clock className="h-5 w-5" />}
              Add 24 Hours
            </Button>
          </>
        )}
      </Card>
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className="flex justify-between">
      <span className="text-slate-500">{label}</span>
      <span className={`font-data ${bold ? "text-lg font-bold text-slate-900" : "text-slate-700"}`}>
        {value}
      </span>
    </div>
  );
}
